import {
  HD_BODYGRAPH_CLASSIC_COLOR_CONFIG,
  HD_BODYGRAPH_COLOR_CONFIG_PRESETS,
  HdBodygraphColorConfig,
} from '../bodygraph/hd-bodygraph-color-config';
import {
  HD_PLANET_GRID_CLASSIC_COLOR_CONFIG,
  HD_PLANET_GRID_COLOR_CONFIG_PRESETS,
  HD_PLANET_GRID_WARM_DUSK_SUBTLE_COLOR_CONFIG,
  HdPlanetGridColorConfig,
} from '../bodygraph/planet-grid/hd-planet-grid-color-config';
import { hdPrintFirstName } from './hd-chart-print';

/** `app` = het kleurenschema dat de gebruiker nu in de app heeft; anders een bodygraph-preset-id. */
export const HD_PRINT_COLOR_SCHEME_APP = 'app';

export type HdChartPrintNameMode = 'firstName' | 'fullName' | 'custom';
export type HdChartPrintLayout = 'basic' | 'regular' | 'mandala';

/** Keuzes uit de printdialoog. `name` is de tekst die in de kop komt (al ingevuld of aangepast). */
export interface HdChartPrintOptions {
  readonly colorScheme: string;
  readonly nameMode: HdChartPrintNameMode;
  readonly name: string;
  readonly layout: HdChartPrintLayout;
  readonly centerKeynotes: boolean;
}

export interface HdChartPrintColors {
  readonly colorConfig: HdBodygraphColorConfig;
  readonly planetGridColorConfig: HdPlanetGridColorConfig;
}

/**
 * Kleuren voor de print. `app` (of een onbekend schema) = de huidige app-kleuren. Een preset
 * kleurt de bodygraph; de planeetgrid volgt het gelijknamige planeetgrid-schema, en waar dat niet
 * bestaat (Enhanced Deep/Light) Warm Dusk Subtle (wens Steven 2026-10-22). Altijd op papier: zie
 * {@link onPaper}.
 */
export function resolveHdChartPrintColors(scheme: string, app: HdChartPrintColors): HdChartPrintColors {
  const preset = HD_BODYGRAPH_COLOR_CONFIG_PRESETS.find(candidate => candidate.id === scheme);
  if (scheme === HD_PRINT_COLOR_SCHEME_APP || preset == null) return onPaper(app);
  const planetGrid = HD_PLANET_GRID_COLOR_CONFIG_PRESETS.find(candidate => candidate.id === preset.id);
  return onPaper({
    colorConfig: preset.config,
    planetGridColorConfig: planetGrid?.config ?? HD_PLANET_GRID_WARM_DUSK_SUBTLE_COLOR_CONFIG,
  });
}

/**
 * Geen gebroken wit op een wit blad (wens Steven 2026-10-22): het warme wit van Warm Dusk oogt daar
 * beige. Open en ongedefinieerde centers en ongedefinieerde planeetblokken krijgen de neutrale
 * grijstinten van het klassieke schema; de centerkleuren zelf blijven die van het schema.
 */
function onPaper(colors: HdChartPrintColors): HdChartPrintColors {
  return {
    colorConfig: {
      ...colors.colorConfig,
      centerUndefined: HD_BODYGRAPH_CLASSIC_COLOR_CONFIG.centerUndefined,
      centerOpen: HD_BODYGRAPH_CLASSIC_COLOR_CONFIG.centerOpen,
    },
    planetGridColorConfig: {
      ...colors.planetGridColorConfig,
      undefinedBackgroundColor: HD_PLANET_GRID_CLASSIC_COLOR_CONFIG.undefinedBackgroundColor,
    },
  };
}

/** De naam die een keuze oplevert; bij `custom` blijft de eigen tekst staan. */
export function hdChartPrintName(mode: HdChartPrintNameMode, chartName: string, custom: string): string {
  if (mode === 'firstName') return hdPrintFirstName(chartName);
  if (mode === 'fullName') return chartName.trim();
  return custom;
}
